import { useContext, useEffect, type ReactNode } from 'react';
import { drawElements } from '../../elements/layout_elements.ts';
import { lastNumber, readingPoints } from '../../values/readings.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { TILE_FONT } from '../../view/theme.ts';
import { captionOfRules, styleOfRules, TileStyleOverrideContext, useResolvedTileStyle, useTileTextColor } from '../../view/tile_style.ts';
import { transformValue } from '../../values/transform.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { useTween } from '../../view/tween.ts';
import { DEFAULT_VALUE_COLOR, GAUGE_MOVE_MS, gaugeValues, valueTileContent, valueTileElements, withGaugeValues, type ValueTile } from './value_tile.ts';

/** The content area of a `value` tile, with the live value of its source. */
export function ValueTileView({ tile }: { tile: ValueTile }) : ReactNode {
    const { options } = tile;
    const environment = useTileEnvironment();
    const icon = useLegacyIcon(options.gauge?.icon);
    // A list's last reading (SP-5); the sparkline reads the value's own source unless it has one (SP-4).
    const readings = environment.useReadings(tile, options.source);
    const value = lastNumber(readings);
    const secondaryValue = lastNumber(environment.useReadings(tile, options.gauge?.secondary?.source));
    // The rings' further values (docs/specs/gauge-styles.md).
    const more = environment.useReadingsList(tile, options.gauge?.more.map((each) => each.source) ?? []).map(lastNumber);
    const { gaugeStyle, valueAlign, titleHeight, captionSize, captionColor, captionAlign } = useResolvedTileStyle();
    const ownSparkline = options.sparkline?.source;
    const sparklineReadings = environment.useReadings(tile, ownSparkline);
    const lineReadings = options.sparkline ? (ownSparkline === undefined ? readings : sparklineReadings) : undefined;
    const tileTextColor = useTileTextColor(DEFAULT_VALUE_COLOR);

    // The rules that hold for the value shown (after the transform) change the tile's style (SR-2,
    // SR-3); their text colour also wins over the value's own colour (SR-5).
    const shown = transformValue(value, options.transform);
    const ruleStyle = styleOfRules(options.styleRules, shown);
    const setOverride = useContext(TileStyleOverrideContext);
    const ruleKey = JSON.stringify(ruleStyle ?? null);
    useEffect(() => {
        setOverride(ruleKey === 'null' ? undefined : (JSON.parse(ruleKey) as typeof ruleStyle));
    }, [ruleKey, setOverride]);
    useEffect(() => () => setOverride(undefined), [setOverride]);
    const textColor = ruleStyle?.text ?? tileTextColor;

    const content = valueTileContent(
        ruleStyle?.text ? { ...tile, options: { ...options, color: ruleStyle.text } } : tile,
        { value, secondary: secondaryValue, more },
        { icon, locale: environment.locale, textColor, gaugeStyle, valueAlign, caption: captionOfRules(options.styleRules, shown), captionHeight: (titleHeight * captionSize) / 100, captionColor, captionAlign },
    );
    if (options.sparkline) {
        const { color, lineWidth, fill } = options.sparkline;
        content.sparkline = { points: readingPoints(lineReadings), color, lineWidth, fill };
    }
    // The gauge moves to a new value (docs/specs/gauge-styles.md §7).
    const moving = useTween(gaugeValues(content), GAUGE_MOVE_MS);

    return (
        <CanvasView
            // Everything drawn (the gauge's colours and the sparkline's options included, for the editor);
            // the sparkline's readings by identity.
            deps={[JSON.stringify({ ...content, sparkline: undefined }), JSON.stringify(options.sparkline), icon, lineReadings]}
            animate={moving.isMoving}
            draw={(ctx, width, height, now) => {
                drawElements(ctx, { left: 0, top: 0, width, height }, valueTileElements(withGaugeValues(content, moving.at(now)), TILE_FONT));
            }}
        />
    );
}
